import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createBlog, updateBlog, getBlog, deleteBlog } from '../../utils/adminApi';
import { motion } from 'framer-motion';
import ImageUpload from '../../components/admin/ImageUpload';
import { 
  FiSave, 
  FiArrowLeft, 
  FiEye, 
  FiUpload,
  FiLink,
  FiCalendar,
  FiTag,
  FiStar,
  FiFileText,
  FiVideo,
  FiClock,
  FiUser,
  FiX
} from 'react-icons/fi';
import { FaVideo, FaNewspaper } from 'react-icons/fa';
import { formatDate } from '../../utils/helpers';

// Helper to scope CSS style tags inside HTML content to avoid leakage
const scopeCSS = (htmlContent, containerClass) => {
  if (!htmlContent) return '';
  return htmlContent.replace(/<style([^>]*)>([\s\S]*?)<\/style>/gi, (match, attrs, css) => {
    const scopedCss = css.replace(/([^\r\n,{}]+)(?=\s*{[^{}]*})/g, (selector) => {
      const trimmed = selector.trim();
      if (!trimmed) return selector;
      if (trimmed.startsWith('@')) return selector;
      
      return trimmed
        .split(',')
        .map(s => {
          const part = s.trim();
          if (!part) return '';
          if (part === 'body' || part === 'html') {
            return `.${containerClass}`;
          }
          return `.${containerClass} ${part}`;
        })
        .join(', ');
    });
    return `<style${attrs}>${scopedCss}</style>`;
  });
};

const AdminBlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blog, setBlog] = useState({
    title: '',
    excerpt: '',
    content: '',
    video_url: '',
    video_description: '',
    duration: '',
    author: 'Deeksha',
    category: 'Nutrition',
    type: 'blog',
    tags: [],
    image: '',
    thumbnail: '',
    read_time: '',
    featured: false,
    published: true,
    meta_description: '',
    publish_date: new Date().toISOString().split('T')[0]
  });
  
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  const categories = [
    'Nutrition',
    'Disease Management', 
    'Lifestyle',
    'Meal Prep',
    'Recipes',
    'Fitness',
    'Mental Health',
    'Weight Management'
  ];

  useEffect(() => {
    if (isEditing) {
      fetchBlog();
    }
  }, [id, isEditing]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/blogs/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBlog({
          ...data.blog,
          publish_date: data.blog.publish_date ? new Date(data.blog.publish_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        });
      } else {
        console.error('Failed to fetch blog');
        navigate('/admin/blogs');
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setBlog(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !blog.tags.includes(tagInput.trim())) {
      setBlog(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setBlog(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };



  const validateForm = () => {
    const newErrors = {};

    if (!blog.title.trim()) newErrors.title = 'Title is required';
    if (!blog.excerpt.trim()) newErrors.excerpt = 'Excerpt is required';
    if (!blog.category) newErrors.category = 'Category is required';

    if (blog.type === 'blog') {
      if (!blog.content.trim()) newErrors.content = 'Content is required for blog posts';
      if (!blog.read_time || blog.read_time <= 0) newErrors.read_time = 'Read time is required for blog posts';
    } else if (blog.type === 'vlog') {
      if (!blog.video_url.trim()) newErrors.video_url = 'Video URL is required for vlogs';
      if (!blog.duration.trim()) newErrors.duration = 'Duration is required for vlogs';
      if (!blog.video_description.trim()) newErrors.video_description = 'Video description is required for vlogs';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (isDraft = false) => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      const blogData = {
        ...blog,
        published: isDraft ? false : true,
        publish_date: new Date(blog.publish_date).toISOString()
      };

      console.log('Saving blog with data:', { 
        id: isEditing ? id : 'new',
        published: blogData.published,
        isDraft,
        title: blogData.title 
      });

      let result;
      if (isEditing) {
        result = await updateBlog(id, blogData);
      } else {
        result = await createBlog(blogData);
      }
      
      console.log('Save result:', result);
      alert(`Blog ${isDraft ? 'saved as draft' : 'published'} successfully!`);
      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error saving blog:', error);
      alert(`Error saving blog: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim('-');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/blogs')}
            className="p-2 text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              {isEditing ? 'Edit Post' : 'Create New Post'}
            </h1>
            <p className="text-neutral-600 mt-1">
              {blog.type === 'vlog' ? 'Create or edit a video blog post' : 'Create or edit a blog article'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isEditing && (
            <button
              onClick={async () => {
                if (window.confirm(`Are you sure you want to delete "${blog.title}"? This action cannot be undone.`)) {
                  try {
                    setSaving(true);
                    await deleteBlog(id);
                    navigate('/admin/blogs');
                  } catch (error) {
                    console.error('Error deleting blog:', error);
                    alert(`Error deleting blog: ${error.message || 'Unknown error'}`);
                    setSaving(false);
                  }
                }
              }}
              disabled={saving}
              className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              Delete
            </button>
          )}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 text-neutral-600 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors flex items-center gap-2"
          >
            <FiEye className="w-4 h-4" />
            {showPreview ? 'Hide' : 'Preview'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="px-4 py-2 text-neutral-600 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
          >
            Save as Draft
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <FiSave className="w-4 h-4" />
            {saving ? 'Saving...' : (blog.published ? 'Update & Publish' : 'Save & Publish')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Basic Information</h3>
            
            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={blog.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.title ? 'border-red-500' : 'border-neutral-200'
                }`}
                placeholder="Enter post title..."
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Excerpt */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Excerpt *
              </label>
              <textarea
                value={blog.excerpt}
                onChange={(e) => handleInputChange('excerpt', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.excerpt ? 'border-red-500' : 'border-neutral-200'
                }`}
                rows={3}
                placeholder="Brief description of your post..."
              />
              {errors.excerpt && <p className="text-red-500 text-sm mt-1">{errors.excerpt}</p>}
            </div>

            {/* Content Type Toggle */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Content Type
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleInputChange('type', 'blog')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    blog.type === 'blog'
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  <FaNewspaper className="w-4 h-4" />
                  Blog Article
                </button>
                <button
                  onClick={() => handleInputChange('type', 'vlog')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    blog.type === 'vlog'
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  <FaVideo className="w-4 h-4" />
                  Video Blog
                </button>
              </div>
            </div>
          </div>

          {/* Content Section */}
          {blog.type === 'blog' ? (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Article Content</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={blog.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.content ? 'border-red-500' : 'border-neutral-200'
                  }`}
                  rows={15}
                  placeholder="Write your blog content here... You can use HTML tags for formatting."
                />
                {errors.content && <p className="text-red-500 text-sm mt-1">{errors.content}</p>}
                <p className="text-sm text-neutral-500 mt-2">
                  You can use HTML tags like &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt; for formatting.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Read Time *
                </label>
                <input
                  type="text"
                  value={blog.read_time}
                  onChange={(e) => handleInputChange('read_time', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.read_time ? 'border-red-500' : 'border-neutral-200'
                  }`}
                  placeholder="e.g., 5 min read"
                />
                {errors.read_time && <p className="text-red-500 text-sm mt-1">{errors.read_time}</p>}
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Video Content</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Video URL *
                </label>
                <input
                  type="url"
                  value={blog.video_url}
                  onChange={(e) => handleInputChange('video_url', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.video_url ? 'border-red-500' : 'border-neutral-200'
                  }`}
                  placeholder="https://www.youtube.com/embed/VIDEO_ID"
                />
                {errors.video_url && <p className="text-red-500 text-sm mt-1">{errors.video_url}</p>}
                <p className="text-sm text-neutral-500 mt-2">
                  Use YouTube embed URL format: https://www.youtube.com/embed/VIDEO_ID
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Duration *
                </label>
                <input
                  type="text"
                  value={blog.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.duration ? 'border-red-500' : 'border-neutral-200'
                  }`}
                  placeholder="e.g., 12:30"
                />
                {errors.duration && <p className="text-red-500 text-sm mt-1">{errors.duration}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Video Description *
                </label>
                <textarea
                  value={blog.video_description}
                  onChange={(e) => handleInputChange('video_description', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.video_description ? 'border-red-500' : 'border-neutral-200'
                  }`}
                  rows={8}
                  placeholder="Describe your video content, include timestamps, key points, etc..."
                />
                {errors.video_description && <p className="text-red-500 text-sm mt-1">{errors.video_description}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publishing Options */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Publishing</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Publish Date
              </label>
              <input
                type="date"
                value={blog.publish_date}
                onChange={(e) => handleInputChange('publish_date', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Author
              </label>
              <input
                type="text"
                value={blog.author}
                onChange={(e) => handleInputChange('author', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Category *
              </label>
              <select
                value={blog.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.category ? 'border-red-500' : 'border-neutral-200'
                }`}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
            </div>

            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="published"
                  checked={blog.published}
                  onChange={(e) => handleInputChange('published', e.target.checked)}
                  className="mr-2 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="published" className="text-sm text-neutral-700">
                  Publish immediately
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featured"
                  checked={blog.featured}
                  onChange={(e) => handleInputChange('featured', e.target.checked)}
                  className="mr-2 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="featured" className="text-sm text-neutral-700">
                  Mark as featured
                </label>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Tags</h3>
            
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  className="flex-1 px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Add a tag..."
                />
                <button
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="text-primary-500 hover:text-primary-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Media */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Media</h3>
            
            {blog.type === 'blog' ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Featured Image
                </label>
                <ImageUpload
                  endpoint="/admin/upload/image"
                  category="blog"
                  prefix="featured"
                  label="Upload Featured Image"
                  currentImage={blog.image}
                  onUploadSuccess={(data) => {
                    console.log('Featured image upload success:', data);
                    const imageUrl = data.image_urls?.original || data.image_urls?.medium || data.file_url;
                    handleInputChange('image', imageUrl);
                  }}
                  onUploadError={(error) => {
                    console.error('Featured image upload error:', error);
                  }}
                  onImageRemove={() => handleInputChange('image', '')}
                  maxSize={5}
                  className="w-full"
                />
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Video Thumbnail
                </label>
                <ImageUpload
                  endpoint="/admin/upload/image"
                  category="blog"
                  prefix="thumbnail"
                  label="Upload Video Thumbnail"
                  currentImage={blog.thumbnail}
                  onUploadSuccess={(data) => {
                    console.log('Thumbnail upload success:', data);
                    const imageUrl = data.image_urls?.original || data.image_urls?.medium || data.file_url;
                    handleInputChange('thumbnail', imageUrl);
                  }}
                  onUploadError={(error) => {
                    console.error('Thumbnail upload error:', error);
                  }}
                  onImageRemove={() => handleInputChange('thumbnail', '')}
                  maxSize={5}
                  className="w-full"
                />
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">SEO</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Meta Description
              </label>
              <textarea
                value={blog.meta_description}
                onChange={(e) => handleInputChange('meta_description', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                placeholder="Brief description for search engines..."
                maxLength={160}
              />
              <p className="text-xs text-neutral-500 mt-1">
                {blog.meta_description.length}/160 characters
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                URL Slug
              </label>
              <input
                type="text"
                value={generateSlug(blog.title)}
                readOnly
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg bg-neutral-50 text-neutral-600"
                placeholder="auto-generated-from-title"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-neutral-900">Preview Post</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 text-neutral-600 hover:text-neutral-800 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                  blog.type === 'vlog' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {blog.type === 'vlog' ? <FaVideo className="w-3 h-3" /> : <FaNewspaper className="w-3 h-3" />}
                  {blog.type === 'vlog' ? 'Video Blog' : 'Blog Article'}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-neutral-900 mb-2">{blog.title || 'Untitled Post'}</h1>
              <div className="flex items-center gap-4 text-sm text-neutral-600 mb-4">
                <span>By {blog.author}</span>
                <span>{formatDate(blog.publish_date)}</span>
                <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full">
                  {blog.category}
                </span>
                {blog.type === 'blog' && blog.read_time && (
                  <span>{blog.read_time}</span>
                )}
                {blog.type === 'vlog' && blog.duration && (
                  <span>{blog.duration}</span>
                )}
              </div>
              <p className="text-lg text-neutral-700 mb-6">{blog.excerpt}</p>
              
              {blog.type === 'vlog' && blog.video_url && (
                <div className="mb-6">
                  <div className="aspect-video bg-neutral-200 rounded-lg flex items-center justify-center">
                    <iframe
                      src={blog.video_url}
                      className="w-full h-full rounded-lg"
                      frameBorder="0"
                      allowFullScreen
                      title={blog.title}
                    />
                  </div>
                </div>
              )}
              
              {blog.type === 'blog' && blog.image && (
                <div className="mb-6">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div className="prose max-w-none">
                {blog.type === 'blog' && (
                  <div 
                    className="blog-content-container"
                    dangerouslySetInnerHTML={{ __html: scopeCSS(blog.content || '<p>No content added yet.</p>', 'blog-content-container') }} 
                  />
                )}
                {blog.type === 'vlog' && (
                  <div className="whitespace-pre-wrap">{blog.video_description || 'No video description added yet.'}</div>
                )}
              </div>
              
              {blog.tags && blog.tags.length > 0 && (
                <div className="mt-6 pt-6 border-t border-neutral-200">
                  <h4 className="text-sm font-medium text-neutral-700 mb-2">Tags:</h4>
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-neutral-100 text-neutral-600 text-sm rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogForm;